import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faCircleInfo,
  faMagnifyingGlass,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { useAppNavigate, useAppType } from '../../context/AppContext'
import { useLayout } from '../../context/LayoutContext'
import { BRANDS } from '../../utils/brand'
import { celebrate, seasonalIcon, useTapCounter } from '../../utils/easterEggs'
import { haptic } from '../../utils/fx'
import { RouteKind } from '../../utils/useRouteKind'
import StatusPill from '../status-pill/StatusPill'
import './AppHeader.scss'

type Props = {
  route: RouteKind
  isDesktop: boolean
}

const c = 'app-header'
const season = seasonalIcon()

export const scrollToMenu = () => {
  const menu = document.getElementById('menu-top')
  if (!menu) return
  const top = menu.getBoundingClientRect().top + window.scrollY - 8
  if (window.scrollY < top) window.scrollTo({ top, behavior: 'smooth' })
}

const AppHeader = ({ route, isDesktop }: Props) => {
  const { t } = useTranslation()
  const navigate = useAppNavigate()
  const appType = useAppType()
  const brand = BRANDS[appType]
  const { searchValue, setSearchValue, openInfo } = useLayout()
  const searchTimeout = useRef<number>()
  const logoRef = useRef<HTMLImageElement>(null)
  const countLogoTap = useTapCounter(7, celebrate)

  function onLogoClick() {
    logoRef.current?.animate(
      [
        { transform: 'rotate(0)' },
        { transform: 'rotate(-14deg) scale(1.1)' },
        { transform: 'rotate(10deg)' },
        { transform: 'rotate(0)' },
      ],
      { duration: 450, easing: 'ease-out' },
    )
    if (countLogoTap()) return
    if (route === 'home') window.scrollTo({ top: 0, behavior: 'smooth' })
    else navigate('/')
  }

  function goBack() {
    haptic()
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) navigate(-1)
    else navigate(route === 'checkout' ? '/cart' : '/')
  }

  function onSearch(value: string) {
    setSearchValue(value)
    if (route !== 'home') navigate('/')
    window.clearTimeout(searchTimeout.current)
    searchTimeout.current = window.setTimeout(scrollToMenu, 450)
  }

  const showBack = !isDesktop && (route === 'cart' || route === 'checkout')
  const pageTitle =
    route === 'cart' ? t('CART') : route === 'checkout' ? t('DELIVERY') : ''

  return (
    <header
      className={`${c} ${c}--${route}${isDesktop ? ` ${c}--desktop` : ''}`}
    >
      <div className={`${c}__inner`}>
        {showBack ? (
          <>
            <button
              className={`${c}__icon-btn`}
              onClick={goBack}
              aria-label={t('BACK')}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div className={`${c}__title`}>{pageTitle}</div>
          </>
        ) : (
          <button className={`${c}__brand`} onClick={onLogoClick}>
            <img ref={logoRef} src={brand.mascot} alt='' draggable={false} />
            <span className={`${c}__wordmark`}>
              {brand.first}
              <b>{brand.second}</b>
              {season && (
                <sup aria-hidden>
                  <FontAwesomeIcon icon={season} />
                </sup>
              )}
            </span>
          </button>
        )}

        {isDesktop && (
          <nav className={`${c}__nav`}>
            <button
              className={route === 'home' ? 'is-active' : ''}
              onClick={() => navigate('/')}
            >
              {t('MENU')}
            </button>
            <button
              className={route === 'delivery' ? 'is-active' : ''}
              onClick={() => navigate('/delivery-conditions')}
            >
              {t('DELIVERY_CONDITIONS')}
            </button>
          </nav>
        )}

        <div className={`${c}__spacer`} />

        {isDesktop && (
          <label className={`${c}__search${searchValue ? ' has-value' : ''}`}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              value={searchValue}
              placeholder={t('SEARCH')}
              onChange={(e) => onSearch(e.target.value)}
              aria-label='search'
            />
            {searchValue && (
              <button
                type='button'
                onClick={() => setSearchValue('')}
                aria-label={t('CLEAR_SEARCH')}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </label>
        )}

        {(isDesktop || (route !== 'checkout' && route !== 'cart')) && (
          <StatusPill onClick={openInfo} />
        )}

        {isDesktop && (
          <button className={`${c}__info`} onClick={openInfo}>
            <FontAwesomeIcon icon={faCircleInfo} />
            {t('INFO')}
          </button>
        )}
      </div>
    </header>
  )
}

export default AppHeader
